import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Info, Sunrise, ChevronLeft, ChevronRight, X, SlidersHorizontal, Inbox, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "./Button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

export function KpiTile({ label, value, sub, delta, notCounted = false, onClick, testid }) {
  const up = delta && /^[▲+]/.test(delta);
  return (
    <button onClick={onClick} data-testid={testid || "kpi-tile"} className={cn("text-left bg-card border border-border rounded-card shadow-card p-5 font-ui hover:bg-muted transition-colors duration-120 min-w-[160px]", notCounted && "text-muted-foreground")}>
      <div className="text-ui-xs text-muted-foreground">{label}</div>
      <div className={cn("font-display font-medium text-ui-kpi mt-1", notCounted ? "text-muted-foreground" : "text-foreground")}>{value}</div>
      <div className={cn("text-ui-xs mt-1", delta ? (up ? "text-success" : "text-destructive") : "text-muted-foreground")}>{delta || sub || (notCounted ? "not counted" : "")}</div>
    </button>
  );
}

export function FilterButton({ count = 0, onClick, children = "Filter" }) {
  return <Button variant={count ? "outline-strong" : "secondary"} size="sm" icon={SlidersHorizontal} onClick={onClick} data-testid="filter-button">{children}{count ? ` ${count}` : ""}</Button>;
}
export function FilterChip({ label, onRemove, onClick }) {
  return (
    <span className="inline-flex items-center h-ctl-sm rounded-chip border border-border bg-card text-ui-xs font-ui" data-testid="filter-chip">
      <button onClick={onClick} className="px-2 hover:bg-muted h-full">{label}</button>
      <button onClick={onRemove} aria-label="Remove filter" className="px-1.5 h-full text-muted-foreground hover:text-foreground border-l border-border"><X className="w-3 h-3" /></button>
    </span>
  );
}

export function EmptyState({ variant = "default", title, body, action, onAction, testid = "empty-state" }) {
  const Icon = variant === "error" ? AlertCircle : Inbox;
  return (
    <div data-testid={testid} className="flex flex-col items-start gap-2 py-8 px-4 font-ui">
      <Icon className={cn("w-6 h-6", variant === "error" ? "text-destructive" : "text-muted-foreground")} />
      <div className="text-ui-base text-foreground">{title}</div>
      {body && <div className="text-ui-sm text-muted-foreground">{body}</div>}
      {action && <Button size="sm" variant="secondary" onClick={onAction}>{action}</Button>}
    </div>
  );
}

export function Skeleton({ variant = "row", n = 3, hint = "Still loading…" }) {
  const [slow, setSlow] = useState(false);
  useEffect(() => { const t = setTimeout(() => setSlow(true), 4000); return () => clearTimeout(t); }, []);
  const shape = variant === "kpi" ? "h-[88px] w-[160px]" : variant === "field" ? "h-5 w-40" : "h-row w-full";
  return (
    <div className="space-y-2 font-ui" data-testid="skeleton">
      {Array.from({ length: n }).map((_, i) => <div key={i} className={cn("v2-skel", shape)} />)}
      {slow && <div className="text-ui-xs text-muted-foreground" data-testid="skeleton-hint">{hint}</div>}
    </div>
  );
}

export function undoToast(message, onUndo, { duration = 8000 } = {}) {
  return toast(message, { duration, action: { label: "Undo", onClick: onUndo } });
}
export function UndoToast({ message, onUndo }) {
  return <div className="flex items-center gap-3 bg-card border border-border rounded-card shadow-popover px-3 h-ctl-md text-ui-sm font-ui"><span>{message}</span><Button size="sm" variant="link" onClick={onUndo}>Undo</Button></div>;
}

export function ConfirmDialog({ open, onOpenChange, title, body, confirmLabel = "Confirm", cancelLabel = "Cancel", destructive = true, onConfirm }) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="rounded-card shadow-dialog font-ui max-w-[440px]" data-testid="confirm-dialog">
        <AlertDialogHeader><AlertDialogTitle className="font-display text-ui-md">{title}</AlertDialogTitle>{body && <AlertDialogDescription className="text-ui-sm">{body}</AlertDialogDescription>}</AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-chip h-ctl-md">{cancelLabel}</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} className={cn("rounded-chip h-ctl-md", destructive ? "bg-destructive hover:brightness-95" : "bg-primary hover:bg-primary-hover")} data-testid="confirm-dialog-ok">{confirmLabel}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function InfoPopover({ title, body, link, linkLabel = "Learn more ›" }) {
  return (
    <Popover>
      <PopoverTrigger asChild><button aria-label={`About ${title}`} className="inline-flex items-center justify-center w-4 h-4 rounded-full text-muted-foreground hover:text-foreground" data-testid="info-popover"><Info className="w-3.5 h-3.5" /></button></PopoverTrigger>
      <PopoverContent className="rounded-card shadow-popover w-72 font-ui text-ui-sm" side="top">
        <div className="font-medium text-foreground mb-1">{title}</div><div className="text-muted-foreground">{body}</div>
        {link && <a href={link} target="_blank" rel="noreferrer" className="text-primary hover:underline text-ui-xs mt-2 inline-block">{linkLabel}</a>}
      </PopoverContent>
    </Popover>
  );
}

export function MorningBrief({ greeting, line, lines = [], counts = [], testid = "morning-brief" }) {
  const [more, setMore] = useState(false);
  return (
    <div data-testid={testid} className="flex items-start gap-3 bg-muted border-b border-hairline px-4 py-2 font-ui text-ui-sm">
      <Sunrise className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="truncate"><span className="font-medium">{greeting}</span>{line ? ` ${line}` : ""}</div>
        {more && lines.map((l, i) => <div key={i} className="text-muted-foreground">{l}</div>)}
      </div>
      {counts.length > 0 && <div className="text-muted-foreground flex-shrink-0">{counts.join(" · ")}</div>}
      {lines.length > 0 && <button onClick={() => setMore((m) => !m)} className="text-primary hover:underline flex-shrink-0" data-testid={`${testid}-more`}>{more ? "Less" : "More"}</button>}
    </div>
  );
}

export function FromPartnerCard({ partner = "Loan Network", initials = "LN", date, kind = "bankQuery", text, onPrimary, onSecondary, testid = "from-partner-card" }) {
  const labels = { bankQuery: ["Bank query", "Reply", "Ask customer"], upload: ["New upload", "Review", "Mark seen"], update: ["Update", "Acknowledge", null] };
  const [k, p, s] = labels[kind] || labels.update;
  return (
    <div data-testid={testid} className="border border-border rounded-card bg-card shadow-card p-4 font-ui text-ui-sm">
      <div className="flex items-center gap-2 text-ui-xs text-muted-foreground mb-1"><span className="w-[22px] h-[22px] rounded-[2px] bg-muted text-foreground inline-flex items-center justify-center font-display font-medium text-[10px]">{initials}</span>From {partner}<span className="flex-1" />{date}</div>
      <div className="text-foreground"><span className="font-medium">{k}</span> · {text}</div>
      <div className="flex gap-1.5 mt-2"><Button size="sm" variant="outline-strong" onClick={onPrimary}>{p}</Button>{s && <Button size="sm" variant="ghost" onClick={onSecondary}>{s}</Button>}</div>
    </div>
  );
}

export function QuarterPicker({ value, onPrev, onNext }) {
  return (
    <div className="inline-flex items-center border border-border rounded-chip h-ctl font-ui text-ui-sm" data-testid="quarter-picker">
      <button onClick={onPrev} aria-label="Previous quarter" className="px-2 h-full hover:bg-muted"><ChevronLeft className="w-4 h-4" /></button>
      <span className="px-3 font-display font-medium border-x border-border h-full inline-flex items-center">{value}</span>
      <button onClick={onNext} aria-label="Next quarter" className="px-2 h-full hover:bg-muted"><ChevronRight className="w-4 h-4" /></button>
    </div>
  );
}
export function WeekStepper({ value, onPrev, onNext }) {
  return (
    <div className="inline-flex items-center gap-1 font-ui text-ui-xs text-muted-foreground" data-testid="week-stepper">
      <button onClick={onPrev} aria-label="Previous week" className="p-1 hover:text-foreground"><ChevronLeft className="w-3.5 h-3.5" /></button>{value}
      <button onClick={onNext} aria-label="Next week" className="p-1 hover:text-foreground"><ChevronRight className="w-3.5 h-3.5" /></button>
    </div>
  );
}

export function Avatar({ name = "", size = "sm", partner = false, picture, extra }) {
  const px = { xs: "w-4 h-4 text-[8px]", sm: "w-[22px] h-[22px] text-[10px]", md: "w-7 h-7 text-ui-xs" }[size];
  const initials = extra != null ? `+${extra}` : name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
  if (picture) return <img src={picture} alt={name} className={cn(px, partner ? "rounded-[2px]" : "rounded-full", "object-cover")} />;
  return <span title={name} className={cn(px, "inline-flex items-center justify-center bg-muted text-foreground font-display font-medium border border-border", partner ? "rounded-[2px]" : "rounded-full")} data-testid="avatar">{initials || "?"}</span>;
}

export function FileThumb({ src, ext = "PDF", status = "guess", selected = false, onClick, name }) {
  const dot = { guess: "bg-success", unsure: "bg-warning-bg", notdoc: "bg-destructive", partner: "bg-muted-foreground" }[status] || "bg-muted-foreground";
  return (
    <button onClick={onClick} title={name} data-testid="file-thumb" className={cn("relative w-24 h-16 rounded-[4px] border bg-muted overflow-hidden flex-shrink-0 transition-colors duration-120", selected ? "border-primary ring-2 ring-ring ring-offset-2" : "border-border hover:border-foreground")}>
      {src ? <img src={src} alt="" className="w-full h-full object-cover" /> : <span className="absolute inset-0 flex items-center justify-center text-ui-xs text-muted-foreground font-display">{ext}</span>}
      <span className="absolute bottom-1 left-1 text-[9px] font-display font-medium bg-card/90 px-1 rounded-[2px]">{ext}</span>
      <span className={cn("absolute top-1 right-1 w-2 h-2 rounded-full", dot)} />
    </button>
  );
}
